import React, { createContext, useCallback, useContext, useMemo } from 'react'
import { endOfDay, getTime, isAfter, isBefore, startOfDay } from 'date-fns'
import { omitBy, isUndefined } from '@golden/utils'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import DateInputBase, { DateInputValue } from '../../default/form/DateInput'
import FormStateProvider from '../../default/form/FormStateProvider'
import FormField from '../../default/form/FormField'
import { searchToRequest, Request } from '../../../views/admin/finance/FinanceWithdrawalAccountBlacklistPage'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import { InitialFormFunc, useRequestFromSearch, useChangeUrlSubmit } from '../../../utils/default/ComplexFlowHook'
import { createDefaultFormState, ValueGetter, FormValidation } from '../../../utils/default/FormHook'
import { convertEmptyToUndefined, getValueFromChangeEvent, getTimeFromDateInputValue, getValueFromValue, getBankAccountInputProps } from '../../../utils/default/FormHelper'
import allRoute from '../route/route'
import FormSubmitButton from '../../default/form/FormSubmitButton'
import { createValidateStartAtWithEndAt } from '../../../utils/default/Validator'

export interface FinanceWithdrawalAccountBlacklistFormType {
  time: DateInputValue
  player_name: string
  account: string
  player_account: string
  memo: string
}

const initialForm: InitialFormFunc<FinanceWithdrawalAccountBlacklistFormType> = (defaultForm) => ({
  time: {
    start: null,
    end: null
  },
  player_name: '',
  account: '',
  player_account: '',
  memo: '',
  ...defaultForm
})

const formToRequest = (form: FinanceWithdrawalAccountBlacklistFormType): Request => {
  const converted = {
    start_at: form.time.start === null ? undefined : getTime(form.time.start),
    end_at: form.time.end === null ? undefined : getTime(form.time.end),
    account: convertEmptyToUndefined(form.account),
    player_account: convertEmptyToUndefined(form.player_account),
    player_name: convertEmptyToUndefined(form.player_name),
    memo: convertEmptyToUndefined(form.memo),
    page: 1
  } as Request
  return omitBy(converted, isUndefined) as Request
}

const getValueFromEvent: ValueGetter<FinanceWithdrawalAccountBlacklistFormType> = {
  time: getTimeFromDateInputValue,
  player_name: getValueFromChangeEvent,
  account: getValueFromChangeEvent,
  player_account: getValueFromChangeEvent,
  memo: getValueFromChangeEvent
}

const FormContext = createContext(createDefaultFormState(initialForm()))

const TextField = React.memo(MuiTextField)

const playerNameInputProps = {
  maxLength: 20
}

const DateInput: React.FC = React.memo(() => {
  const classes = useCommonStyles()
  const { t } = useT()
  const { value, handleChange } = useContext(FormContext)
  const {
    shouldDisableStartDate,
    shouldDisableEndDate
  } = useMemo(() => {
    const shouldDisableStartDate = (day: Date | null): boolean => {
      if (day === null) return true
      if (value.time.end === null) return false
      return isAfter(day, endOfDay(value.time.end))
    }
    const shouldDisableEndDate = (day: Date | null): boolean => {
      if (day === null) return true
      if (value.time.start === null) return false
      return isBefore(day, startOfDay(value.time.start))
    }
    return {
      shouldDisableStartDate,
      shouldDisableEndDate
    }
  }, [value.time])
  const startOption = useMemo(() => ({
    label: t('common.beginAt'),
    shouldDisableDate: shouldDisableStartDate
  }), [shouldDisableStartDate, t])

  const endOption = useMemo(() => ({
    label: t('common.endAt'),
    shouldDisableDate: shouldDisableEndDate
  }), [shouldDisableEndDate, t])

  const dateClasses = useMemo(() => ({
    button: classes.pinkGradualButton
  }), [classes.pinkGradualButton])

  const onChange = useCallback(handleChange('time'), [])
  return (
    <DateInputBase
      value={value.time}
      onChange={onChange}
      start={startOption}
      end={endOption}
      classes={dateClasses}
    />
  )
})

const FinanceWithdrawalAccountBlacklistForm: React.FC = () => {
  const classes = useCommonStyles()
  const { t } = useT()
  const request = useRequestFromSearch({ searchToRequest })
  const defaultValue = useMemo(() => initialForm({
    time: {
      start: request?.start_at !== undefined ? new Date(request.start_at) : null,
      end: request?.end_at !== undefined ? new Date(request.end_at) : null
    },
    ...request
  }), [request])

  const handleSubmit = useChangeUrlSubmit({
    toAddNowTimestamp: true,
    formToRequest,
    encodePath: allRoute.financeWithdrawalAccountBlacklist.encodePath
  })

  const formValidation: FormValidation<FinanceWithdrawalAccountBlacklistFormType> = useMemo(() => {
    return {
      time: [],
      player_name: [],
      account: [],
      player_account: [],
      memo: []
    }
  }, [t])

  return (
    <Box padding={4}>
      <Box
        paddingY={1.25}
        paddingX={2}
        className={classes.pinkTitleBar}
      >
        <Typography variant="h5">
          {t('page.alipayBlacklist')}
        </Typography>
      </Box>
      <FormStateProvider
        context={FormContext}
        defaultValue={defaultValue}
        onSubmit={handleSubmit}
        getValueFromEvent={getValueFromEvent}
        validation={formValidation}
      >
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <DateInput />
          </Grid>
          <Grid item>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} md={4}>
                <FormField<FinanceWithdrawalAccountBlacklistFormType, TextFieldProps>
                  context={FormContext}
                  component={TextField}
                  name="player_name"
                  label={t('common.realName')}
                  placeholder={t('placeholder.inputRealName')}
                  inputProps={playerNameInputProps}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormField<FinanceWithdrawalAccountBlacklistFormType, TextFieldProps>
                  context={FormContext}
                  component={TextField}
                  name="account"
                  label={t('common.alipayAccount')}
                  placeholder={t('placeholder.inputSearchAlipayAccount')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormField<FinanceWithdrawalAccountBlacklistFormType, TextFieldProps>
                  context={FormContext}
                  component={TextField}
                  name="player_account"
                  label={t('common.playerAccount')}
                  placeholder={t('placeholder.inputPlayerAccount')}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} md={4}>
                <FormField<FinanceWithdrawalAccountBlacklistFormType, TextFieldProps>
                  context={FormContext}
                  component={TextField}
                  name="memo"
                  label={t('common.memo')}
                  placeholder={t('placeholder.inputMemo')}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container direction="row" justifyContent="flex-end">
              <FormSubmitButton
                className={classes.purpleGradualButton}
                context={FormContext}
                type="submit"
                component={Button}
              >
                {t('common.search')}
              </FormSubmitButton>
            </Grid>
          </Grid>
        </Grid>
      </FormStateProvider>
    </Box>
  )
}

export default React.memo(FinanceWithdrawalAccountBlacklistForm)
